// Where a recording's audio is played from, and which version (trim, 2026-10-06).
//
// Every player builds the URL here: /api/recordings/<id>/audio, plus ?v=<audioRev> once the
// recording has been trimmed or restored (Recording.audioRev). A ?v= URL never changes content,
// so the service worker's CacheFirst copy of it is always right; a new trim is a new URL. The
// versions (and the measured playback gains) come from every recording list the app reads
// (lib/rows.ts recordingRow → noteRecording), so a player that only has an id (a "rec:<id>" clip
// ref, a word mark) still gets the current version.
//
// When a recording's version changes, the copies of its older versions are dropped from the
// "recordings" cache (on this device; the partner's device does the same when its list arrives).
import type { Recording } from '@valyrio/engine/api';

/** The service worker's cache of our recordings (vite.config.ts). */
export const RECORDINGS_CACHE = 'recordings';

interface Known {
  rev: string;
  gain?: number;
}
const known = new Map<string, Known>();
const STORE = 'valyrio:rec-versions';
let loaded = false;

function load(): void {
  if (loaded) return;
  loaded = true;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORE) : null;
    const parsed = raw ? (JSON.parse(raw) as Record<string, Known>) : {};
    for (const [id, k] of Object.entries(parsed)) if (k && typeof k.rev === 'string') known.set(id, k);
  } catch {
    /* nothing remembered: the next list fills it */
  }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function save(): void {
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try {
      localStorage.setItem(STORE, JSON.stringify(Object.fromEntries(known)));
    } catch {
      /* storage full or blocked: kept for this page only */
    }
  }, 200);
}

const listeners = new Set<() => void>();
/** Notified when a version or gain changes (players re-read). */
export function onRecordingAudioChange(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
function emit(): void {
  for (const l of listeners) l();
}

// ---------- takes still queued on this device (recordings/outbox.ts) ----------

/** A take queued on this device, not on the server yet: "pending:<outboxId>". */
export const PENDING_PREFIX = 'pending:';
export const isPendingTake = (id: string): boolean => id.startsWith(PENDING_PREFIX);

/** Object URLs of the queued takes' audio, so every player plays them from this device. */
const pendingUrls = new Map<string, string>();
/** Remember a queued take's audio (once per id; the URL lives as long as the page). */
export function setPendingAudioUrl(id: string, audio: Blob): void {
  if (pendingUrls.has(id) || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return;
  try {
    pendingUrls.set(id, URL.createObjectURL(audio));
  } catch {
    /* no object URLs here (tests): the take does not play until it has been sent */
  }
}

/** The plain path (no version): what the cache keys of a recording start with. */
export const recordingAudioPath = (id: string): string => `/api/recordings/${encodeURIComponent(id)}/audio`;

/** The URL to play a recording from: its current version when known. */
export function recordingAudioUrl(id: string): string {
  const local = pendingUrls.get(id);
  if (local) return local;
  load();
  const rev = known.get(id)?.rev;
  return rev ? `${recordingAudioPath(id)}?v=${encodeURIComponent(rev)}` : recordingAudioPath(id);
}

/** A recording's audio URL with the version it carries itself (newer than the registry's, if anything). */
export function audioUrlOf(rec: Pick<Recording, 'id' | 'audioRev'>): string {
  const local = pendingUrls.get(rec.id);
  if (local) return local;
  return rec.audioRev ? `${recordingAudioPath(rec.id)}?v=${encodeURIComponent(rec.audioRev)}` : recordingAudioUrl(rec.id);
}

/** The recording id of a /api/recordings/<id>/audio URL (absolute or not), else null. */
export function recordingIdOfUrl(url: string): string | null {
  const m = /\/api\/recordings\/([^/?#]+)\/audio(?:[?#]|$)/.exec(url);
  return m ? decodeURIComponent(m[1]!) : null;
}

/** The measured playback gain of a recording's current audio (undefined: not measured yet). */
export function recordingGain(id: string): number | undefined {
  load();
  return known.get(id)?.gain;
}

/** The version the app knows for a recording ('' = as first uploaded). */
export function recordingRev(id: string): string {
  load();
  return known.get(id)?.rev ?? '';
}

/**
 * Remember a recording's audio version and gain (called for every recording row the app reads).
 * A changed version drops the cached copies of the older ones.
 */
export function noteRecording(rec: Pick<Recording, 'id' | 'audioRev' | 'gain'>): void {
  load();
  if (!rec.id) return;
  const before = known.get(rec.id);
  const rev = rec.audioRev ?? '';
  const gain = typeof rec.gain === 'number' && Number.isFinite(rec.gain) ? rec.gain : undefined;
  if (before && before.rev === rev && before.gain === gain) return;
  // Versions sort in time order (the API's revStamp): an older cached list never steps back.
  if (before && rev < before.rev) return;
  if (!rev && gain === undefined) {
    if (!before) return;
    known.delete(rec.id);
  } else {
    known.set(rec.id, gain === undefined ? { rev } : { rev, gain });
  }
  save();
  if (before && before.rev !== rev) void dropStaleCopies(rec.id, rev);
  emit();
}

/** A measured gain for the audio of `rev` (stored locally; lib queues it for the server). */
export function setRecordingGain(id: string, rev: string, gain: number): void {
  load();
  if ((known.get(id)?.rev ?? '') !== rev) return;
  known.set(id, { rev, gain });
  save();
  emit();
}

/** Drop cached copies of a recording other than version `keep` ('' = keep none but the plain URL's). */
export async function dropStaleCopies(id: string, keep: string | null): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const cache = await caches.open(RECORDINGS_CACHE);
    for (const req of await cache.keys()) {
      const u = new URL(req.url);
      if (recordingIdOfUrl(u.pathname) !== id || u.searchParams.get('original') === '1') continue;
      if (keep !== null && (u.searchParams.get('v') ?? '') === keep) continue;
      await cache.delete(req);
    }
  } catch {
    /* no cache here */
  }
}

/** Tests: forget everything. */
export function __resetRecordingAudio(): void {
  known.clear();
  pendingUrls.clear();
  loaded = true;
  listeners.clear();
}
